<template>
  <ul v-if="finalCrumbs.length" class="breadcrumb size-xs">
    <li>
      <NuxtLink to="/">{{ siteName }}</NuxtLink>
    </li>
    <li v-for="(crumb, index) in finalCrumbs" :key="index">
      <template v-if="index < finalCrumbs.length - 1 && crumb.path">
        <NuxtLink :to="strippedURL(crumb.path)" external>{{
          crumb.title
        }}</NuxtLink>
      </template>
      <template v-else>
        {{ crumb.title }}
      </template>
    </li>
  </ul>
</template>

<script setup>
const config = useRuntimeConfig();
const datasourcesStore = useDatasourcesStore();
const props = defineProps({
  pageTitle: {
    type: String,
    required: false,
    default: "",
  },
});

const siteName = computed(() => {
  const item = datasourcesStore.entries?.datasource_entries?.find(
    (entry) => entry.name === "site.name"
  );
  const value = item?.value;
  return !value || value === "IS_EMPTY" ? "" : value;
});

const route = useRoute();
let path = route.path;
path = path[path.length - 1] === "/" ? path.slice(0, -1) : path;
const params = path.startsWith("/")
  ? path.substring(1).split("/")
  : path.split("/").filter(Boolean);

// Build crumbs array synchronously (fix forEach async issue)
const crumbs = [];
let crumbPath = "";
params.forEach((param) => {
  crumbPath = `${crumbPath}/${param}`;
  crumbs.push({
    path: crumbPath,
  });
});

// Initialize finalCrumbs immediately with default titles (non-blocking)
// This allows the breadcrumbs to render immediately while API calls happen in background
const finalCrumbs = reactive(
  crumbs.map((crumb, index) => {
    const isLast = index === params.length - 1;
    let title = crumb.path.split("/").slice(-1)[0]?.replace(/-/g, " ") || "";
    title = title ? title[0].toUpperCase() + title.slice(1).toLowerCase() : "";
    
    // Use pageTitle for last crumb if provided
    if (isLast && props.pageTitle) {
      title = props.pageTitle;
    }
    
    return {
      ...crumb,
      title: title,
    };
  })
);

const breadcrumbStructuredDataSeo = computed(() => {
  const itemListElement = [
    {
      "@type": "ListItem",
      position: 1,
      name: siteName.value,
      item: config.public["baseURL"],
    },
    ...finalCrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 2,
      name: crumb.title,
      item: config.public["baseURL"] + crumb.path,
    })),
  ].filter((item) => item.name);

  itemListElement.forEach((item, index) => {
    item.position = index + 1;
  });

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  });
});

useHead({
  script: computed(() => [
    {
      key: "breadcrumb-structured-data",
      type: "application/ld+json",
      innerHTML: breadcrumbStructuredDataSeo.value,
    },
  ]),
});

// Enhance breadcrumbs with Storyblok data asynchronously (non-blocking)
// This runs in the background and updates the breadcrumbs when data arrives
if (crumbs.length > 0) {
  const breadcrumbPromises = crumbs.map((item, index) => {
    return useCustomAsyncStoryblok("", {
      version: config.public["storyblokVersion"],
      by_slugs: `${config.public["storyblokCountryPrefix"]}${item.path}/`,
    });
  });

  // Update breadcrumbs when API calls complete (non-blocking)
  Promise.all(breadcrumbPromises)
    .then((results) => {
      results.forEach((result, index) => {
        if (!result || !result.value) {
          return; // Keep default title
        }

        const isLast = index === params.length - 1;
        let finalTitle;
        const breadcrumbLabel = result.value?.stories?.[0]?.content?.breadcrumb_label;
        
        if (isLast && props.pageTitle) {
          finalTitle = props.pageTitle;
        } else if (breadcrumbLabel) {
          finalTitle = breadcrumbLabel;
        } else {
          return; // Keep default title
        }

        if (finalCrumbs[index].title !== finalTitle) {
          finalCrumbs[index].title = finalTitle;
        }
      });
    })
    .catch((error) => {
      console.error("Error loading breadcrumbs:", error);
    });
}
</script>

<style lang="scss" scoped>
.breadcrumb {
  padding: 1.5rem col-span() 0;
  text-align: center;
  @include truncate;
  color: var(--text-70);
  @include for-tablet-landscape-up {
    padding-top: 2.5rem;
  }
  a {
    color: var(--text-70);
    text-decoration: none;
  }
  li {
    display: inline;
  }
  li:not(:last-child):after {
    content: " > ";
    padding: 0 0.2rem;
  }
}
.facility-bar + .breadcrumb {
  padding-top: 1.25rem;
}
</style>
